import React, { useEffect, useRef, useState } from "react";
import { SessionTimer } from "./SessionTimer";
import SessionWarningBanner from "./SessionWarningBanner";
import SessionRecoveryModal from "./SessionRecoveryModal";
import { getIdleLimitMs, getWarnBeforeMs } from "../../utils/sessionConfig";
import { auth } from "../../pages/api/axios";

function isAuthenticated() {
    return !!window.localStorage.getItem("authUser");
}

const SessionTimeoutManager = () => {
    const [authed, setAuthed] = useState(isAuthenticated());
    const [warning, setWarning] = useState(null); // null or { remainingMs }
    const [recovery, setRecovery] = useState(() => auth.getRecoveryState());
    const timerRef = useRef(null);

    useEffect(() => {
        const unsubscribe = auth.subscribeRecovery(setRecovery);
        setRecovery(auth.getRecoveryState());
        return unsubscribe;
    }, []);

    // Track login/logout in THIS tab (rdc-auth-changed, dispatched by the auth saga)
    // and in OTHER tabs (the 'storage' event, which never fires in the writing tab).
    useEffect(() => {
        const recompute = () => setAuthed(isAuthenticated());
        const onStorage = (e) => {
            if (e.key === "authUser") recompute();
        };
        window.addEventListener("storage", onStorage);
        window.addEventListener("rdc-auth-changed", recompute);
        recompute(); // in case login completed between render and this effect
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("rdc-auth-changed", recompute);
        };
    }, []);

    useEffect(() => {
        if (!authed || recovery) return undefined;
        auth.sendHeartbeat();
        const id = setInterval(() => auth.sendHeartbeat(), 60 * 1000);
        return () => clearInterval(id);
    }, [authed, recovery]);

    useEffect(() => {
        if (!authed || recovery) return undefined;

        const timer = new SessionTimer({
            idleLimitMs: getIdleLimitMs(),
            warnBeforeMs: getWarnBeforeMs(),
            onChange: (state) => {
                setWarning(state.phase === "warning" ? { remainingMs: state.remainingMs } : null);
            },
            onExpire: () => {
                setWarning(null);
                auth.lockSession("session_timeout");
            },
        });
        timerRef.current = timer;
        timer.start();
        return () => {
            timer.destroy();
            timerRef.current = null;
            setWarning(null);
        };
    }, [authed, recovery]);

    if (!authed) return null;
    if (recovery) return <SessionRecoveryModal reason={recovery.reason} email={recovery.email} />;
    if (!warning) return null;

    return (
        <SessionWarningBanner
            remainingMs={warning.remainingMs}
            onContinue={() => timerRef.current && timerRef.current.stayActive()}
        />
    );
};

export default SessionTimeoutManager;
